import { LitElement, css, html } from '@lit'

class NovaHeader extends LitElement {
  static properties = {
    isSticky: {
      type: Boolean,
      attribute: 'is-sticky',
    },
    isScrolled: {
      type: Boolean,
      reflect: true,
      attribute: 'is-scrolled',
    },
    isHovered: {
      type: Boolean,
      reflect: true,
      attribute: 'is-hovered',
    },
    isDrawerOpen: {
      type: Boolean,
      reflect: true,
      attribute: 'is-drawer-open',
    },
  }

  constructor() {
    super()
    this.isScrolled = false
    this.isHovered = false
    this.isDrawerOpen = false
    this.resizeObserver = null
    this.resizeTimeout = null
  }

  connectedCallback() {
    super.connectedCallback()

    if (this.isSticky) {
      this.parentElement.classList.add('is-sticky')
    }

    window.addEventListener('scroll', this.onScrollHandler)
    this.addEventListener('mouseover', this.onHoverHandler)
    this.addEventListener('mouseout', this.offHoverHandler)
    window.addEventListener('nova:header-drawer:open', this.headerDrawerOpen)
    window.addEventListener('nova:header-drawer:close', this.headerDrawerClose)

    requestAnimationFrame(() => {
      this.updateHeaderHeightVariable()
    })

    // Handle window resize with debounce
    window.addEventListener('resize', this.handleResize)

    // Observe element resize changes
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateHeaderHeightVariable()
      })
      this.resizeObserver.observe(this)
    } else {
      console.warn('ResizeObserver is not supported in this browser. Falling back to no-op.')
      this.resizeObserver = {
        observe: () => {},
        disconnect: () => {}
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('scroll', this.onScrollHandler)
    this.removeEventListener('mouseover', this.onHoverHandler)
    this.removeEventListener('mouseout', this.offHoverHandler)
    window.removeEventListener('nova:header-drawer:open', this.headerDrawerOpen)
    window.removeEventListener('nova:header-drawer:close', this.headerDrawerClose)

    window.removeEventListener('resize', this.handleResize)

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = null
    }
  }

  handleResize = () => {
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      this.updateHeaderHeightVariable()
    }, 300)
  }

  updateHeaderHeightVariable() {
    const headerHeight = this.getBoundingClientRect().height
    document.documentElement.style.setProperty('--nova-header-height', `${headerHeight}px`)
    document.documentElement.style.setProperty('--window-height', `${window.innerHeight}px`);
    window.headerHeight = headerHeight

    const headerAnnouncementBar = this.querySelector('[data-js-target="js-announcement-bar"]')
    if (headerAnnouncementBar) {
      const announcementBarHeight = headerAnnouncementBar.getBoundingClientRect().height
      document.documentElement.style.setProperty('--nova-header-announcement-bar-height', `${announcementBarHeight}px`)
      window.announcementBarHeight = announcementBarHeight
    } else {
      document.documentElement.style.setProperty('--nova-header-announcement-bar-height', '0px')
      window.announcementBarHeight = 0
    }
  }

  onScrollHandler = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    if (scrollTop > 0) {
      // Scrolled below the fold
      if (!this.isScrolled) {
        this.isScrolled = true
      }
    } else {
      // Scrolled above the fold
      if (this.isScrolled) {
        this.isScrolled = false
      }
    }
  }

  onHoverHandler = () => {
    if (!this.isHovered) {
      this.isHovered = true
    }
  }

  offHoverHandler = () => {
    if (this.isHovered) {
      this.isHovered = false
    }
  }

  headerDrawerOpen = () => {
    if (!this.isDrawerOpen) {
      this.isDrawerOpen = true
    }
  }

  headerDrawerClose = () => {
    if (this.isDrawerOpen) {
      this.isDrawerOpen = false
    }
  }

  render() {
    return html`
      <div class="container">
        <slot></slot>
      </div>
    `
  }

  static styles = css`
    :host {
      display: block;
      position: relative;
      background-color: var(--header-bg-color);
      transition: background-color 250ms;
      z-index: var(--nova-z-index-sticky);
    }

    :host([is-sticky]) {
      position: sticky;
      top: 0;
      left: 0;
      right: 0;
    }

    .container {
      background-color: var(--nova-background, transparent);
      left: 0;
      top: 0;
      transform: translateY(0);
      transition: transform var(--nova-duration-long, 1000ms);
      width: 100%;
      z-index: 10;
      position: relative;
    }
  `
}

customElements.define('nova-header', NovaHeader)
